/* eslint-disable react/no-danger */
import { useState } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import cx from 'classnames';
import { Button, Rating, Tag, Icon, ExpandableLink } from '@bwoty-web/ui-kit';
import { UI_CHECKMARK } from '@bwoty-web/ui-kit/icons';
import { scrollUpToElementIfNeeded } from '../../utils/elements';
import QuickFacts from './quickFacts';
import Title from '../../common/title';
import './cruiseShipDescriptionView.scss';

const CruiseShipDescriptionView = ({ cruiseShipContent, labels }) => {
  const {
    name,
    facts = [],
    rating,
    shipDescription,
    facilities = [],
    cruiseBadge,
    shippingCompanyContent,
    tag,
    shippingCompanyUrl,
  } = cruiseShipContent;
  const isGlobe = useIsGlobetrotter();
  const [isExpanded, setIsExpanded] = useState(false);

  const shipDescriptionText =
    shipDescription &&
    shipDescription?.length > 0 &&
    shipDescription.map((item) => {
      return (
        <div
          key={uniqid('cruise-ship-description-paragraph-')}
          className={cx('gw-cruise-ship-description-paragraph', {
            'gw-cruise-ship-description-paragraph--expanded': isExpanded,
          })}
          dangerouslySetInnerHTML={{
            __html: item.content,
          }}
        />
      );
    });

  const handleToggleExpansion = () => {
    setIsExpanded(!isExpanded);
    const [containerElement] = document.getElementsByClassName(
      'gw-cruise-ship-description-content',
    );
    scrollUpToElementIfNeeded(containerElement);
  };

  const ExpandButton = isGlobe ? Button : ExpandableLink;

  return (
    <div className="gw-cruise-ship-description">
      <div className="gw-cruise-ship-description-content uikit-box">
        <div
          className={cx('gw-cruise-ship-description-container', {
            'gw-cruise-ship-description-container--expanded': isExpanded,
            'gw-cruise-ship-description-container--has-tags': !!tag,
          })}
        >
          <div className="gw-cruise-ship-description__titles">
            {tag && (
              <div className="gw-cruise-ship-tags">
                <Tag variant={isGlobe ? 'quartz' : 'black'}>{tag}</Tag>
              </div>
            )}
            <Title className="gw-cruise-ship-description__title">{name}</Title>
            <div className="gw-cruise-ship-description__subtitle-and-rating">
              {shippingCompanyUrl ? (
                <a
                  className="gw-cruise-ship-description__sub-title"
                  href={shippingCompanyUrl}
                >
                  {shippingCompanyContent?.name}
                </a>
              ) : (
                <h3 className="gw-cruise-ship-description__sub-title">
                  {shippingCompanyContent?.name}
                </h3>
              )}
              <Rating
                className="gw-cruise-ship-description-rating"
                rating={rating * 2}
                size="m"
              />
            </div>
          </div>
          <div className="gw-cruise-ship-description__description">
            <div className="gw-cruise-ship-description__description-title">
              {labels?.aboutCruiseshipTitle}
            </div>
            {shipDescriptionText}
            <div className="gw-cruise-ship-description__read-more">
              <ExpandButton
                data-gtm-tag="Read More"
                onClick={handleToggleExpansion}
                id="gtm-cruise-ship-page-expandbutton"
                isOpen={isGlobe ? undefined : isExpanded}
              >
                {isExpanded ? labels?.showLessButton : labels?.readMoreButton}
              </ExpandButton>
            </div>
          </div>
          <QuickFacts
            className="gw-cruise-ship-description__quick-facts"
            facts={facts}
            factsHeader={labels?.quickFactsHeader}
            cruiseBadge={cruiseBadge}
            shippingCompanyUrl={shippingCompanyUrl}
          />
        </div>
      </div>
      {facilities.length > 0 && (
        <>
          <h2 className="gw-cruise-ship-description__facilities-header">
            {labels?.facilitiesHeader}
          </h2>
          <div className="gw-cruise-ship-description__facilities-content uikit-box">
            {facilities.map((item) => {
              return (
                <div className="gw-cruise-ship-description__facility-category" key={uniqid('facility-list-')}>
                  <h3 className="gw-cruise-ship-description__facility-list-header">
                    {item.header}
                  </h3>
                  <ul className="gw-cruise-ship-description__facility-list">
                    {item.paragraphs.map((paragraph) => {
                      return (
                        <li
                          key={uniqid('facility-list-li-')}
                          className="gw-cruise-ship-description__facility-li"
                        >
                          <div>
                            <Icon
                              className="gw-cruise-ship-description__facility-icon"
                              path={UI_CHECKMARK}
                              size="xs"
                              aria-hidden="true"
                            />
                          </div>
                          <p>{paragraph.content}</p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

CruiseShipDescriptionView.propTypes = {
  cruiseShipContent: PropTypes.shape({
    name: PropTypes.string,
    rating: PropTypes.number,
    tag: PropTypes.string,
    shippingCompanyUrl: PropTypes.string,
    facts: PropTypes.arrayOf(
      PropTypes.shape({
        factId: PropTypes.number,
        factTxtValue: PropTypes.string,
        factName: PropTypes.string,
      }),
    ),
    facilities: PropTypes.arrayOf(
      PropTypes.shape({
        header: PropTypes.string,
        paragraphs: PropTypes.arrayOf(
          PropTypes.shape({
            content: PropTypes.string,
          }),
        ),
      }),
    ),
    shipDescription: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string,
      }),
    ),
    cruiseBadge: PropTypes.shape({
      sizes: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    shippingCompanyContent: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  labels: PropTypes.shape({
    quickFactsHeader: PropTypes.string,
    facilitiesHeader: PropTypes.string,
    aboutCruiseshipTitle: PropTypes.string,
    readMoreButton: PropTypes.string,
    showLessButton: PropTypes.string,
  }).isRequired,
};

export default CruiseShipDescriptionView;
